const React = require('react');
const { createElement: h, useState } = React;
const UsageHistory = require('./UsageHistory');
const { isLocalDev } = require('../lib/devMode');
require('./UsagePage.css');

var PLAN_LABELS = { free: 'Free Trial', pro: 'Pro', max: 'Max', enterprise: 'Enterprise' };
var PLAN_COLORS = { free: '#94a3b8', pro: '#a67c52', max: '#7c3aed', enterprise: '#0f172a' };

function formatTokenCount(n) {
  if (!n || n < 1) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
}

function formatCredits(n) {
  if (n === null || n === undefined) return 'Ubegrænset';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  if (n >= 100) return Math.round(n).toString();
  if (n >= 1) return n.toFixed(1);
  if (n > 0) return n.toFixed(2);
  return '0';
}

function formatDate(d) {
  if (!d) return '-';
  var date = new Date(d);
  if (!Number.isFinite(date.getTime())) return '-';
  var months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return date.getDate() + '. ' + months[date.getMonth()] + ' ' + date.getFullYear();
}

var MODEL_LABELS = {
  'claude-sonnet-4-6': 'Claude Sonnet',
  'claude-sonnet-4-6-20250514': 'Claude Sonnet',
  'claude-haiku-4-5': 'Claude Haiku',
  'claude-haiku-4-5-20251001': 'Claude Haiku',
  'gpt-5_4-mini': 'GPT 5.4 Mini',
  'gpt-4o-mini': 'GPT 4o Mini'
};

// Unknown models fall back to their raw key. Keys are stored with dots escaped
// as underscores (chatAdapter trackTokenUsage), so undo that for readability:
// "gpt-5_4-mini" → "gpt-5.4-mini".
function getModelLabel(key) {
  return MODEL_LABELS[key] || String(key).replace(/_/g, '.');
}

// The five metered surfaces plus the catch-all, in the order they render.
// Keys match utils/featureContext.js on the API side.
var FEATURE_LABELS = {
  copilot: 'Copilot',
  planner: 'Planner',
  uiplanner: 'UI Planner',
  codegen: 'Kodegenerering',
  askcode: 'AskCode',
  other: 'Andet'
};

function getFeatureLabel(key) {
  return FEATURE_LABELS[key] || key;
}

// Every token the call consumed. Cache reads and writes are billed (reads at
// ~0.1x, writes at ~1.25x) and on a cached agent loop they dwarf the plain
// input/output counts — leaving them out understates real usage several-fold.
function bucketTotal(m) {
  if (!m) return 0;
  return (m.inputTokens || 0)
    + (m.outputTokens || 0)
    + (m.cacheReadTokens || 0)
    + (m.cacheWriteTokens || 0);
}

// Horizontal bar sized against the heaviest bucket, so the biggest consumer
// is obvious at a glance rather than needing the numbers compared by eye.
function UsageBar(props) {
  var total = props.total || 0;
  var max = props.max || 0;
  var pct = max > 0 ? Math.max((total / max) * 100, 1) : 0;
  var share = props.grandTotal > 0 ? Math.round((total / props.grandTotal) * 100) : 0;

  return h('div', { className: 'usp-usage-bar' },
    h('div', { className: 'usp-usage-bar__head' },
      h('span', { className: 'usp-usage-bar__name' }, props.label),
      h('span', { className: 'usp-usage-bar__total' },
        formatTokenCount(total),
        h('span', { className: 'usp-usage-bar__share' }, ' · ' + share + '%')
      )
    ),
    h('div', { className: 'usp-usage-bar__track' },
      h('div', { className: 'usp-usage-bar__fill', style: { width: pct + '%' } })
    ),
    h('div', { className: 'usp-usage-bar__stats' },
      h('span', null, 'In ' + formatTokenCount(props.data.inputTokens || 0)),
      h('span', null, 'Out ' + formatTokenCount(props.data.outputTokens || 0)),
      h('span', null, 'Cache in ' + formatTokenCount(props.data.cacheReadTokens || 0)),
      h('span', null, 'Cache out ' + formatTokenCount(props.data.cacheWriteTokens || 0))
    )
  );
}

function ProgressBar(props) {
  var used = props.used || 0;
  var limit = props.limit;
  var unlimited = limit === null || limit === undefined;
  var pct = unlimited ? 0 : (limit > 0 ? Math.min((used / limit) * 100, 100) : 0);
  var isWarning = !unlimited && pct >= 75;
  var isDanger = !unlimited && pct >= 90;
  var barColor = isDanger ? '#ef4444' : isWarning ? '#f59e0b' : '#a67c52';

  return h('div', { className: 'usp-progress' },
    h('div', { className: 'usp-progress__header' },
      h('span', { className: 'usp-progress__label' }, props.label || ''),
      h('span', { className: 'usp-progress__count' },
        unlimited
          ? formatCredits(used) + ' brugt'
          : formatCredits(used) + ' / ' + formatCredits(limit)
      )
    ),
    h('div', { className: 'usp-progress__track' },
      h('div', {
        className: 'usp-progress__fill',
        style: { width: (unlimited ? 0 : pct) + '%', background: barColor }
      })
    ),
    isDanger ? h('p', { className: 'usp-progress__warning' }, 'Du nærmer dig din grænse. Overvej at opgradere din plan.') : null
  );
}

function UsagePage(props) {
  var user = props && props.user ? props.user : null;
  var workflows = Array.isArray(props && props.workflows) ? props.workflows : [];
  var onResetUsage = props && props.onResetUsage ? props.onResetUsage : function() {};
  var tokenUsage = user && user.tokenUsage ? user.tokenUsage : { inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0 };
  var totalTokens = bucketTotal(tokenUsage);
  var byModel = user && user.tokenUsageByModel ? user.tokenUsageByModel : {};
  // Heaviest model first — the list can hold a dozen models after a dev session.
  var modelKeys = Object.keys(byModel).sort(function(a, b) {
    return bucketTotal(byModel[b]) - bucketTotal(byModel[a]);
  });
  // Per-surface split: which of copilot / planner / UI planner / codegen /
  // AskCode is actually burning the tokens. Heaviest first, same as models.
  var byFeature = user && user.tokenUsageByFeature ? user.tokenUsageByFeature : {};
  var featureKeys = Object.keys(byFeature).sort(function(a, b) {
    return bucketTotal(byFeature[b]) - bucketTotal(byFeature[a]);
  });
  var featureMax = featureKeys.length ? bucketTotal(byFeature[featureKeys[0]]) : 0;
  var [resetting, setResetting] = useState(false);

  var plan = user && user.plan ? user.plan : 'free';
  var planLimits = user && user.planLimits ? user.planLimits : {};
  var creditsUsed = user && user.creditsUsed !== undefined ? user.creditsUsed : 0;
  var creditsResetAt = user && user.creditsResetAt ? user.creditsResetAt : null;
  var nodeExecutionsUsed = user && user.nodeExecutionsUsed !== undefined ? user.nodeExecutionsUsed : 0;
  var workflowCount = workflows.length;

  // Visibility toggles — kept as flags (not deleted) so these blocks can be
  // brought back by flipping to true.
  var SHOW_CREDITS_RESET = false;        // "Nulstilles <dato>" badge next to the plan
  // Developer tools: token totals, per-model breakdown and the reset button
  // only render on localhost. The matching API route is localhost-only too.
  var SHOW_TOKEN_USAGE = isLocalDev();
  var SHOW_RESET_BUTTON = isLocalDev();

  function handleReset() {
    if (resetting) return;
    setResetting(true);
    Promise.resolve(onResetUsage()).then(function() {
      setResetting(false);
    }).catch(function() {
      setResetting(false);
    });
  }

  return h('div', { className: 'usp-content' },
    h('header', { className: 'usp-hero' },
      h('p', { className: 'usp-hero__eyebrow' }, 'Konto'),
      h('h1', { className: 'usp-hero__title' }, 'Forbrug'),
      h('p', { className: 'usp-hero__copy' }, 'Overvåg dit forbrug af kreditter, workflows og AI-ressourcer.')
    ),

    // Plan badge
    h('div', { className: 'usp-section usp-plan-section' },
      h('div', { className: 'usp-plan-badge', style: { borderColor: PLAN_COLORS[plan] || '#94a3b8' } },
        h('span', { className: 'usp-plan-badge__label' }, 'Aktiv plan'),
        h('span', { className: 'usp-plan-badge__name', style: { color: PLAN_COLORS[plan] || '#94a3b8' } }, PLAN_LABELS[plan] || plan),
        (SHOW_CREDITS_RESET && creditsResetAt)
          ? h('span', { className: 'usp-plan-badge__reset' }, 'Nulstilles ' + formatDate(creditsResetAt))
          : null
      )
    ),

    // Usage progress bars
    h('div', { className: 'usp-section' },
      h('h2', { className: 'usp-section__title' }, 'Forbrugsoversigt'),
      h('div', { className: 'usp-progress-grid' },
        h(ProgressBar, {
          label: 'Kreditter',
          used: creditsUsed,
          limit: planLimits.monthlyCredits
        }),
        h(ProgressBar, {
          label: 'Knude-eksekveringer',
          used: nodeExecutionsUsed,
          limit: planLimits.monthlyNodeExecutions
        }),
        h(ProgressBar, {
          label: 'Workflows',
          used: workflowCount,
          limit: planLimits.maxWorkflows
        })
      )
    ),

    // Execution history (charts + drill-down)
    h(UsageHistory, { workflows: workflows }),

    // Feature access
    h('div', { className: 'usp-section' },
      h('h2', { className: 'usp-section__title' }, 'Funktioner'),
      h('div', { className: 'usp-features' },
        h('div', { className: 'usp-feature-row' },
          h('span', { className: 'usp-feature-row__icon usp-feature-row__icon--' + (planLimits.scheduling ? 'on' : 'off') },
            planLimits.scheduling ? '✓' : '✗'
          ),
          h('span', { className: 'usp-feature-row__label' }, 'Tidsplanlægning'),
          !planLimits.scheduling ? h('span', { className: 'usp-feature-row__upgrade' }, 'Pro+') : null
        ),
        h('div', { className: 'usp-feature-row' },
          h('span', { className: 'usp-feature-row__icon usp-feature-row__icon--' + (planLimits.publicRunUrls ? 'on' : 'off') },
            planLimits.publicRunUrls ? '✓' : '✗'
          ),
          h('span', { className: 'usp-feature-row__label' }, 'Offentlige kør-URL\'er'),
          !planLimits.publicRunUrls ? h('span', { className: 'usp-feature-row__upgrade' }, 'Pro+') : null
        )
      )
    ),

    // Token breakdown
    SHOW_TOKEN_USAGE && h('div', { className: 'usp-section' },
      h('h2', { className: 'usp-section__title' }, 'Token-forbrug'),
      h('div', { className: 'usp-token-cards' },
        h('div', { className: 'usp-token-card' },
          h('span', { className: 'usp-token-card__label' }, 'Input Tokens'),
          h('span', { className: 'usp-token-card__value' }, formatTokenCount(tokenUsage.inputTokens))
        ),
        h('div', { className: 'usp-token-card' },
          h('span', { className: 'usp-token-card__label' }, 'Output Tokens'),
          h('span', { className: 'usp-token-card__value' }, formatTokenCount(tokenUsage.outputTokens))
        ),
        h('div', { className: 'usp-token-card' },
          h('span', { className: 'usp-token-card__label' }, 'Cache-læsning'),
          h('span', { className: 'usp-token-card__value' }, formatTokenCount(tokenUsage.cacheReadTokens))
        ),
        h('div', { className: 'usp-token-card' },
          h('span', { className: 'usp-token-card__label' }, 'Cache-skrivning'),
          h('span', { className: 'usp-token-card__value' }, formatTokenCount(tokenUsage.cacheWriteTokens))
        ),
        h('div', { className: 'usp-token-card usp-token-card--total' },
          h('span', { className: 'usp-token-card__label' }, 'Total Tokens'),
          h('span', { className: 'usp-token-card__value' }, formatTokenCount(totalTokens))
        )
      )
    ),

    // Feature breakdown — which product surface is eating the tokens
    SHOW_TOKEN_USAGE && featureKeys.length > 0 && h('div', { className: 'usp-section' },
      h('h2', { className: 'usp-section__title' }, 'Forbrug pr. funktion'),
      h('div', { className: 'usp-usage-chart' },
        featureKeys.map(function(key) {
          var f = byFeature[key] || {};
          return h(UsageBar, {
            key: key,
            label: getFeatureLabel(key),
            data: f,
            total: bucketTotal(f),
            max: featureMax,
            grandTotal: totalTokens
          });
        })
      )
    ),

    // Model breakdown
    SHOW_TOKEN_USAGE && modelKeys.length > 0 && h('div', { className: 'usp-section' },
      h('h2', { className: 'usp-section__title' }, 'Forbrug pr. model'),
      h('div', { className: 'usp-model-list' },
        modelKeys.map(function(key) {
          var m = byModel[key] || {};
          var mInput = m.inputTokens || 0;
          var mOutput = m.outputTokens || 0;
          var mCacheRead = m.cacheReadTokens || 0;
          var mCacheWrite = m.cacheWriteTokens || 0;
          var mTotal = bucketTotal(m);
          return h('div', { className: 'usp-model-row', key: key },
            h('span', { className: 'usp-model-row__name' }, getModelLabel(key)),
            h('div', { className: 'usp-model-row__stats' },
              h('span', { className: 'usp-model-row__stat' },
                h('span', { className: 'usp-model-row__stat-label' }, 'In'),
                ' ',
                h('span', { className: 'usp-model-row__stat-value' }, formatTokenCount(mInput))
              ),
              h('span', { className: 'usp-model-row__stat' },
                h('span', { className: 'usp-model-row__stat-label' }, 'Out'),
                ' ',
                h('span', { className: 'usp-model-row__stat-value' }, formatTokenCount(mOutput))
              ),
              h('span', { className: 'usp-model-row__stat' },
                h('span', { className: 'usp-model-row__stat-label' }, 'Cache in'),
                ' ',
                h('span', { className: 'usp-model-row__stat-value' }, formatTokenCount(mCacheRead))
              ),
              h('span', { className: 'usp-model-row__stat' },
                h('span', { className: 'usp-model-row__stat-label' }, 'Cache out'),
                ' ',
                h('span', { className: 'usp-model-row__stat-value' }, formatTokenCount(mCacheWrite))
              ),
              h('span', { className: 'usp-model-row__stat usp-model-row__stat--total' },
                h('span', { className: 'usp-model-row__stat-label' }, 'Total'),
                ' ',
                h('span', { className: 'usp-model-row__stat-value' }, formatTokenCount(mTotal))
              )
            )
          );
        })
      )
    ),

    // Reset button — zeroes tokens, credits and node executions (local dev only)
    SHOW_RESET_BUTTON && h('div', { className: 'usp-section usp-reset-section' },
      h('button', {
        className: 'usp-reset-btn',
        onClick: handleReset,
        disabled: resetting
      }, resetting ? 'Nulstiller...' : 'Nulstil forbrug')
    )
  );
}

module.exports = UsagePage;
